import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';

import boardStyles from '../../../../styles/calls/board.module.css';
import styles from './ProposalCard.module.css';
import Avatar from '../../../../components/UI/Avatar';
import ContactCard from '../../../../components/UI/ContactCard';
import BoardCard from '../../../../components/UI/Board/BoardCard/BoardCard';
import { useAppSelector, useAppDispatch } from 'src/app/hooks';
import { selectContactsMap } from 'slices/contactsSlice';
import { selectIsCurrentWorkspaceCollaborative } from 'slices/workspacesSlice';
import useFormatNumber from 'src/app/hooks/ui/useFormatNumber';
import { setCurrentContact } from 'slices/contactsSlice';
import ProposalEntity from 'src/db/entities/proposal/ProposalEntity';
import DirectedText from '../../../../features2/localization/ui/DirectedText';
import contactService from '../../../../services/contacts/contactService';

// pulled in from your slice
import { DISCOUNT_TYPES } from 'src/app/constants';
import { calcTotal } from 'slices/proposalsSlice';

interface PriceResult {
  total: number;
  subtotal: number;
  subtotalWithDiscount: number;
  tax: number;
}

const ProposalCard = ({
                        proposalData,
                        onContextMenu,
                        menuIsOpen,
                      }: {
  proposalData: ProposalEntity;
  onContextMenu: (x: number, y: number) => void;
  menuIsOpen: boolean;
}) => {
  const { t } = useTranslation('proposals');
  const contacts = useAppSelector(selectContactsMap);
  const isCollaborative = useAppSelector(selectIsCurrentWorkspaceCollaborative);
  const relatedContact = contacts[proposalData.contactId];
  const { formatNumber } = useFormatNumber();
  const dispatch = useAppDispatch();

  // 1. local state for pricing
  const [price, setPrice] = useState<PriceResult | null>(null);

  // 2. effect to fetch whenever these inputs change
  useEffect(() => {
    let mounted = true;

    calcTotal({
      items: proposalData.items,
      includesTax: proposalData.settings.includesTax,
      taxRate: proposalData.settings.taxRate,
      discountAmount: proposalData.discountAmount,
      discountType: proposalData.discountType as DISCOUNT_TYPES,
    })
        .then((result) => {
          if (mounted) setPrice(result);
        })
        .catch((err) => {
          console.error('Failed to load price', err);
        });

    return () => {
      mounted = false;
    };
  }, [
    proposalData.items,
    proposalData.settings.includesTax,
    proposalData.settings.taxRate,
    proposalData.discountAmount,
    proposalData.discountType,
  ]);

  // 3. render loading or the number
  const totalDisplay = price
      ? `${proposalData.settings.currency} ${price.total.toFixed(2)}`
      : t('Card.loading'); // add a "loading" key in your i18n

  return (
      <BoardCard
          onContextMenu={(e) => {
            e.preventDefault();
            onContextMenu(e.clientX, e.clientY);
          }}
          className={clsx(
              boardStyles.BoardCard,
              menuIsOpen && boardStyles.BoardCardMenuOpen,
              styles.BoardCard
          )}
      >
        <ContactCard
            onClick={() => {
              dispatch(setCurrentContact(relatedContact.uuid));
            }}
            className={styles.ContactCard}
        >
          <Avatar
              thumbnail={relatedContact.thumbnail}
              name={relatedContact.suggestion_name || relatedContact.name}
              size={32}
              className={styles.Avatar}
          />
          <div className={styles.content}>
            <div className={styles.mainContent}>
              <div className={styles.name}>
                <span className={styles.number}>#{proposalData.number}</span>{' '}
                {contactService.getContactNameWithFormattedPhoneNumber(
                    relatedContact,
                    formatNumber
                )}
              </div>
              <div className={styles.title}>
                {proposalData.title || proposalData.client}
              </div>
              <div className={styles.totalTitle}>
                <DirectedText>
                <span style={{ textAlign: 'left', display: 'block' }}>
                  {t('Card.total')}:{' '}
                  <span className={styles.totalValue}>{totalDisplay}</span>
                </span>
                </DirectedText>
              </div>
            </div>
            <div className={styles.end}>
              <div
                  className={clsx(
                      styles.sendDate,
                      styles.isDraftLabel
                  )}
              >
                {t(`proposal-form:form.field.status_value.${proposalData.status}`)}
              </div>
            </div>
          </div>
        </ContactCard>
        {isCollaborative && (
            <ContactCard.AssignToSection
                contactId={relatedContact.uuid}
                assignedTo={relatedContact.assigned_to}
            />
        )}
      </BoardCard>
  );
};

export default ProposalCard;
